import React from "react";
import PropTypes from "prop-types";
import { Box, Grid, Typography } from "@mui/material";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import EMPBanner from "../../shared/components/EMPBanner/EMPBanner";
import { capitalizeText, formatToSimpleDate } from "../../utils/EmpUtil";
import { formatPriceValue } from "../../utils/FormatPrice";
import { TRADE_IN_STATUS } from "../../shared/constants/TradeInConstants";
import { LincolnDetailsCard } from "./PurchaseRequestDetails";

const headingSx = {
  fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
  fontSize: "24px",
  fontWeight: 600,
  lineHeight: "32px",
  color: "text.primary",
};

const hasValue = (value) => value != null && String(value).trim() !== "";
const currency = (value) =>
  hasValue(value) && Number.isFinite(Number(value))
    ? formatPriceValue(Number(value))
    : "--";
const answer = (value, t) => {
  if (value === true) return t("tradeIn.yes");
  if (value === false) return t("tradeIn.no");
  return "--";
};

export const getTradeInStatusText = (status, t) => {
  if (status === TRADE_IN_STATUS.ACCEPT_KEEP) return t("tradeIn.acceptAndKeep");
  if (status === TRADE_IN_STATUS.REJECTED) return t("tradeIn.rejected");
  if (status === TRADE_IN_STATUS.ACCEPT_AUCTION)
    return t("tradeIn.acceptAndSend");
  return status;
};

const getAppraisalRows = (tradeIn, ownership, t) => {
  if (ownership === "LEASED") {
    return [
      {
        label: t("tradeIn.quoted"),
        amount: tradeIn.totalOutstandingBalanceAmount,
      },
      {
        label: t("tradeIn.enteredByCustomer"),
        amount: tradeIn.enteredObligation,
      },
    ];
  }
  const rows = [
    {
      label: t("tradeIn.appraisalAmount"),
      amount: tradeIn.appraisalAmount,
    },
  ];
  if (ownership === "FINANCED") {
    const entered = hasValue(tradeIn.enteredBalance);
    const balance = entered ? tradeIn.enteredBalance : tradeIn.remainingBalance;
    rows.push({
      label: t(`tradeIn.${entered ? "enteredBalance" : "remainingBalance"}`),
      // Preserve the existing balance-as-deduction presentation, including zero.
      amount:
        hasValue(balance) && Number.isFinite(Number(balance))
          ? -Number(balance)
          : null,
      subLabel: entered ? t("tradeIn.enteredByCustomer") : null,
    });
  }
  return rows;
};

const getVehicleName = (tradeIn) =>
  [
    tradeIn?.modelYear,
    tradeIn?.vehicleMake,
    tradeIn?.vehicleModel,
    tradeIn?.trim,
  ]
    .filter(hasValue)
    .join(" ") || "--";

// Match the pending decision state used by the Customer Info action card.
// The existing decision modal still enforces its permission/link rules.
const canStartTradeInDecision = (tradeIn, customerReservation) => {
  if (!tradeIn || !customerReservation?.dealerTradeInOptIn) return false;
  if (Object.values(TRADE_IN_STATUS).includes(tradeIn.status)) return false;
  const purchaseStatus =
    customerReservation.purchaseInfo?.acceptRejectStatus?.toLowerCase();
  return tradeIn.status === "evaluate" || purchaseStatus === "accepted";
};

// Figma's Status is offer expiry, not the dealer's decision. Preserve the
// decision independently below instead of conflating the two states.
const getOfferStatus = (tradeInExpired, t) => {
  if (tradeInExpired === true) return t("tradeIn.expired");
  if (tradeInExpired === false) return t("tradeIn.notExpired");
  return "--";
};

const formatConditionRating = (rating) =>
  Number.isFinite(Number(rating)) ? Number(rating).toFixed(1) : "--";

const getVehicleDetails = (tradeIn, ownership, t) => {
  const isLease = ownership === "LEASED";
  const rows = [
    {
      heading: t("tradeIn.fields.0"),
      content: getOfferStatus(tradeIn.tradeInExpired, t),
    },
    { heading: t("tradeIn.fields.5"), content: tradeIn.vin || "--" },
    {
      heading: t("tradeIn.offerExpires"),
      content: tradeIn.expiryDateTimestamp
        ? formatToSimpleDate(tradeIn.expiryDateTimestamp)
        : "--",
    },
    {
      heading: t("tradeIn.modelYear"),
      content: getVehicleName(tradeIn),
    },
    !isLease && {
      heading: t("tradeIn.mileage"),
      content: hasValue(tradeIn.mileage) ? `${tradeIn.mileage} miles` : "--",
    },
    {
      heading: t("tradeIn.fields.4"),
      content: capitalizeText(ownership) || "--",
    },
    !isLease &&
      hasValue(tradeIn.conditionRating) && {
        heading: t("tradeIn.fields.9"),
        content: formatConditionRating(tradeIn.conditionRating),
      },
    tradeIn.status &&
      tradeIn.status !== "evaluate" && {
        heading: t("tradeIn.decisionStatus"),
        content: getTradeInStatusText(tradeIn.status, t),
      },
    tradeIn.rejectedReason && {
      heading: t("tradeIn.fields.1"),
      content: tradeIn.rejectedReason,
    },
    tradeIn.acceptedDateNA && {
      heading: t("tradeIn.fields.2"),
      content: formatToSimpleDate(tradeIn.acceptedDateNA),
    },
    hasValue(tradeIn.userId) && {
      heading: t("tradeIn.fields.3"),
      content: tradeIn.userId,
    },
  ];
  return rows.filter(Boolean);
};

const getConditionDetails = (tradeIn, t) => [
  {
    heading: t("tradeIn.fields.10"),
    content: tradeIn.overallCondition || "--",
  },
  { heading: t("tradeIn.fields.11"), content: answer(tradeIn.hasOdors, t) },
  {
    heading: t("tradeIn.fields.12"),
    content: answer(tradeIn.hasMajorDamage, t),
  },
  {
    heading: t("tradeIn.fields.13"),
    content: answer(tradeIn.hasMinorDamage, t),
  },
  {
    heading: t("tradeIn.fields.14"),
    content: answer(tradeIn.hasIlluminatedWarningLights, t),
  },
  { heading: t("tradeIn.fields.15"), content: answer(tradeIn.hasAccidents, t) },
  // The order DTO does not supply a rental/commercial/rideshare answer.
  { heading: t("tradeIn.commercialUse"), content: "--" },
  {
    heading: t("tradeIn.maintenanceRecords"),
    content: answer(tradeIn.hasMaintenanceHistory, t),
  },
];

const estimateBannerSx = {
  '& [role="alert"]': {
    backgroundColor: "#ECEEEF",
    borderRadius: "8px",
    color: "text.primary",
  },
  "& [role='alert'] .MuiTypography-subtitle1": {
    fontFamily: "ProximaNovaSemibold, Arial, sans-serif",
    fontWeight: 600,
  },
  '& [role="alert"] > .MuiBox-root:first-of-type svg path[fill="#066FEF"]': {
    fill: "#485B65",
  },
};

const startDecisionButtonSx = {
  color: "#485B65",
  backgroundColor: "transparent",
  border: "none",
  borderRadius: 0,
  minWidth: 0,
  px: 0,
  "&:hover": {
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
  "&.Mui-focusVisible": {
    outline: "2px solid #485B65",
    outlineOffset: 3,
  },
};

const amountRowSx = {
  display: "flex",
  justifyContent: "space-between",
  gap: 4,
};

const AppraisalSummary = ({ tradeIn, netEquity, ownership, t }) => (
  <Box component="dl" data-testid="trade-in-appraisal" sx={{ m: 0 }}>
    {getAppraisalRows(tradeIn, ownership, t).map((row) => (
      <Box key={row.label} sx={{ ...amountRowSx, mb: 4 }}>
        <Box>
          <Typography component="dt" variant="body1">
            {row.label}
          </Typography>
          {row.subLabel && (
            <Typography variant="caption">{row.subLabel}</Typography>
          )}
        </Box>
        <Typography
          component="dd"
          variant="body1"
          sx={{ m: 0, whiteSpace: "nowrap" }}
        >
          {currency(row.amount)}
        </Typography>
      </Box>
    ))}
    <Box
      data-testid="trade-in-net-equity"
      sx={{ ...amountRowSx, borderTop: "1px solid #B2B2B2", pt: 4 }}
    >
      <Typography component="dt" variant="body1" sx={headingSx}>
        {t("tradeIn.netTradeInEquity")}
      </Typography>
      <Typography
        component="dd"
        variant="body1"
        sx={{ m: 0, whiteSpace: "nowrap" }}
      >
        {currency(netEquity)}
      </Typography>
    </Box>
  </Box>
);

AppraisalSummary.propTypes = {
  tradeIn: PropTypes.object.isRequired,
  netEquity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ownership: PropTypes.string,
  t: PropTypes.func.isRequired,
};

AppraisalSummary.defaultProps = {
  ownership: undefined,
};

const NoTradeIn = ({ t }) => (
  <Box>
    <Typography component="h2" variant="h3" sx={{ ...headingSx, mb: 4 }}>
      {t("tradeIn.tradeIn")}
    </Typography>
    <Typography variant="body1">{t("tradeIn.noTradeInFound")}</Typography>
  </Box>
);

NoTradeIn.propTypes = {
  t: PropTypes.func.isRequired,
};

const LincolnTradeIn = ({ content, t, onStartDecision }) => {
  const { tradeIn, customerReservation } = content;

  if (!tradeIn) {
    return (
      <Box sx={{ mb: 11 }}>
        <NoTradeIn t={t} />
      </Box>
    );
  }

  const ownership = tradeIn.ownershipType?.toUpperCase();
  const isLease = ownership === "LEASED";

  return (
    <Box sx={{ mb: 11 }}>
      <Box data-testid="trade-in-estimate-banner" sx={estimateBannerSx}>
        <EMPBanner
          title={t("common.banners.estimatedTradeIn.title")}
          content={t("common.banners.estimatedTradeIn.content")}
        />
      </Box>
      <Grid
        container
        rowSpacing={6}
        columnSpacing={{ xs: 6, md: 12 }}
        sx={{ mt: 2 }}
      >
        <Grid item xs={12} md={4}>
          <Typography component="h2" variant="h3" sx={{ ...headingSx, mb: 6 }}>
            {t(
              isLease
                ? "tradeIn.tradeIn_obligation"
                : "tradeIn.appraisalHeading"
            )}
          </Typography>
          <Typography component="h3" variant="h3" sx={{ ...headingSx, mb: 6 }}>
            {getVehicleName(tradeIn)}
          </Typography>
          <AppraisalSummary
            tradeIn={tradeIn}
            netEquity={
              tradeIn.netEquity ?? customerReservation?.tradeIn?.netEquity
            }
            ownership={ownership}
            t={t}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              mb: 4,
            }}
          >
            <Typography component="h2" variant="h3" sx={headingSx}>
              {t("tradeIn.statusHeading")}
            </Typography>
            {canStartTradeInDecision(tradeIn, customerReservation) && (
              <EMPButton
                variant="text"
                onClick={onStartDecision}
                sx={startDecisionButtonSx}
              >
                {t("common.buttonCtas.startTradeInDecision")}
              </EMPButton>
            )}
          </Box>
          <LincolnDetailsCard
            data={getVehicleDetails(tradeIn, ownership, t)}
            columns={2}
            showRowDividers={false}
            testId="trade-in-vehicle-details"
          />
          {!isLease && (
            <Box sx={{ mt: 8 }}>
              <Typography
                component="h2"
                variant="h3"
                sx={{ ...headingSx, mb: 4 }}
              >
                {t("tradeIn.tradeIn_condition")}
              </Typography>
              <LincolnDetailsCard
                data={getConditionDetails(tradeIn, t)}
                columns={2}
                showRowDividers={false}
                testId="trade-in-condition-details"
              />
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

LincolnTradeIn.propTypes = {
  content: PropTypes.shape({
    tradeIn: PropTypes.object,
    customerReservation: PropTypes.object,
  }).isRequired,
  t: PropTypes.func.isRequired,
  onStartDecision: PropTypes.func.isRequired,
};

export default LincolnTradeIn;
